// @flow
import { Log, UserNotifications, isQuotaError, clearImageCaches } from '@helpers/helpers';
import localforage from 'localforage';
import Swal from 'sweetalert2';

const stores = {
  APP_STORE: 'APP_STORE',
};

const getLSItem = () => localStorage.getItem(window.APP_LS_KEY);

class IDBEndinge {
  constructor() {
    window.idb = this;
  }

  /**
   *
   */
  // A storage error fails the start (app.js shows "Recarregar"): swallowing it left the app on its
  // spinner for good. iOS can lose the IndexedDB connection while the app sits in the background.
  fetchInitialState = (): Promise<any> => new Promise((resolve, reject) => {
    const result = () => localforage.getItem(stores.APP_STORE).then(resolve).catch((error) => {
      Log.error(error);
      reject(error);
    });
    if (getLSItem()) {
      this.migrate().then(result, (error) => {
        Log.error(error);
        reject(error);
      });
    } else {
      result();
    }
  });

  // Saves that failed because the device's storage is full, in a row
  storageFullFailures: number = 0;

  // Saves that failed for any other reason, in a row
  saveFailures: number = 0;

  // What the person was told on this page load: each notice at most once
  told: { storageFull: boolean, saveFailed: boolean } = { storageFull: false, saveFailed: false };

  storeState = (state: Object): Promise<any> => new Promise(resolve => {
    const mutableState = state && state.asMutable ? state.asMutable() : state;
    localforage.setItem(stores.APP_STORE, mutableState).then((value) => {
      this.storageFullFailures = 0;
      this.saveFailures = 0;
      resolve(value);
    }).catch((error) => {
      if (isQuotaError(error)) this.onStorageFull(error);
      else this.onSaveFailed();
      Log.error(error);
    });
  });

  // The image caches go first (they refill from S3). A save that still fails after that (the second in a row)
  // says the device has no room left: once, with only OK. The store is saved a second after every change, so a
  // disk that stays full used to bring the blank Sim/Não dialog back every ten failed saves.
  // A dialog already open waits for a later failure: SweetAlert would close it as if answered "Não".
  onStorageFull = (error: any) => {
    clearImageCaches(error);
    this.storageFullFailures += 1;
    if (this.storageFullFailures < 2 || this.told.storageFull || Swal.isVisible()) return;
    this.told.storageFull = true;
    UserNotifications.i18nTextAlert('info/storage_full');
  };

  // Another failure (the stored state can't be written as it is): past 10 in a row, an offer to reload the data
  // from the API, which starts the store over. Once per page load, and not over another dialog.
  onSaveFailed = () => {
    this.saveFailures += 1;
    if (this.saveFailures <= 10 || this.told.saveFailed || !window.clearAppStorage || Swal.isVisible()) return;
    this.told.saveFailed = true;
    UserNotifications.confirmI18n('alert/storage_save_failed').then((confirmed) => {
      if (confirmed) window.clearAppStorage();
    });
  };

  reset = (): Promise<any> => new Promise(resolve => {
    localforage.removeItem(stores.APP_STORE).then(resolve).catch(Log.error);
  });

  // Settles on every path: a migration that never answered held the start on its spinner
  migrate = () => new Promise((resolve, reject) => {
    Log.info('Migrating from old storage');
    let data = null;
    try {
      data = JSON.parse(getLSItem() || 'null');
    } catch (e) {
      resolve();
      return;
    }
    if (!data) {
      resolve();
      return;
    }
    localforage.setItem(stores.APP_STORE, data).then(() => {
      localStorage.removeItem(window.APP_LS_KEY);
      resolve();
    }).catch(reject);
  });
}


export default (): Promise<any> => new Promise((resolve, reject) => {
  localforage.config({
    driver: [
      localforage.INDEXEDDB,
      localforage.WEBSQL,
      localforage.LOCALSTORAGE],
    name: window.APP_LS_KEY,
    version: '1',
  });
  window.localforage = localforage;
  localforage.ready().then(() => {
    resolve(new IDBEndinge());
    let storageEngine = '';

    switch (localforage.driver()) {
      case 'asyncStorage': storageEngine = 'IndexedDB'; break;
      case 'localStorageWrapper': storageEngine = 'LocalStorage'; break;
      case 'webSQLStorage': storageEngine = 'WebSQL'; break;
      default: storageEngine = 'unknown'; break;
    }

    Log.info(`Using ${storageEngine} as storage engine`);
  }).catch((error) => {
    Log.error('No available storage method found');
    reject(error || new Error('No available storage method found'));
  });
});
